import { useRef, useState } from 'react'

import { useSelector } from 'react-redux'

import { ColorWrapper, TextParagraph } from '@atoms'
import { Alert } from '@interco/inter-ui/components/Alert'
import { Button } from '@interco/inter-ui/components/Button'
import { useFeedbackPageTracking, useGoHomeButton, useTrackingRefin } from '@hooks'
import { sendButtonClickTracking } from '@hooks/useFeedbackTracking'
import { BridgeService } from '@services/bridge'
import { ApplicationState } from '@store/types'
import { Page } from '@templates'
import { StepRefin } from '@utils/enums'
import { openLinkByEnviroment } from '@utils/functions'

import {
  ArrowDown,
  ArrowUp,
  Content,
  InstructionItem,
  InstructionList,
  PlayButton,
  ShowMoreText,
  StyledVideo,
  VideoWrapper,
} from './styles'

const instructions = [
  { id: 1, text: 'Acesse o aplicativo Meu INSS;' },
  {
    id: 2,
    text: 'Digite "Desbloqueio de benefício para Empréstimo Consignado" na lupa e escolha a primeira opção;',
  },
  {
    id: 3,
    text: 'Toque na opção "Bloquear/Desbloquear benefício para Empréstimo Consignado"',
  },
  { id: 4, text: 'Se o benefício estiver bloqueado, clique no botão "Avançar";' },
  {
    id: 5,
    text: 'Verifique se você não está em nenhuma das situações listadas. Se tudo estiver correto, clique em "Avançar";',
  },
  { id: 6, text: 'Será necessário realizar um reconhecimento facial, siga as instruções;' },
  {
    id: 7,
    text: 'Complete o formulário com os dados do requerente e clique em "Avançar".',
  },
  {
    id: 8,
    text: 'Após o desbloqueio, retorne ao SuperApp do Inter e continue a sua contratação.',
  },
]

const MEU_INSS_URL = 'https://www.gov.br/pt-br/temas/meu-inss'

const BeneficioBloqueado = () => {
  const isApp = useSelector((state: ApplicationState) => state.ui.navigation.isApp)
  const [expanded, setExpanded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useTrackingRefin(StepRefin.BENEFICIO_BLOQUEADO)
  useFeedbackPageTracking('Como desbloquear benefício', 'Como desbloquear benefício')
  const handleGoHome = useGoHomeButton('btn_voltar_home_como_desbloquear')

  const handleExpand = () => setExpanded(!expanded)
  const handlePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play().catch((_err) => {
        // play() may reject when autoplay is blocked by browser policy
      })
    } else {
      video.pause()
    }
  }

  const visibleInstructions = expanded ? instructions : instructions.slice(0, 2)

  return (
    <Page
      titleHeader="Como desbloquear"
      showLeftIcon={false}
      stickyFooter={
        <>
          <Button
            variant="primary"
            aria-label="Acessar Meu INSS"
            onClick={() => {
              BridgeService.tic({
                name: 'btn_acessar_meu_inss',
                action: 'acessar_meu_inss',
              })
              sendButtonClickTracking(isApp, 'Acessar Meu INSS', MEU_INSS_URL)
              openLinkByEnviroment(MEU_INSS_URL)
            }}
          >
            Acessar Meu INSS
          </Button>
          <Button variant="secondary" aria-label="Ir para Home" onClick={handleGoHome}>
            Ir para Home
          </Button>
        </>
      }
    >
      <Content>
        <ColorWrapper fontWeight="600" style={{ marginBottom: '8px', textAlign: 'left' }}>
          <TextParagraph variant="headline-h1" textAlign="left">
            Desbloquear benefício
          </TextParagraph>
        </ColorWrapper>

        <TextParagraph variant="body-3" textAlign="left">
          É preciso ser o titular da aposentadoria ou pensão.
        </TextParagraph>

        <VideoWrapper>
          <StyledVideo
            ref={videoRef}
            disableRemotePlayback
            disablePictureInPicture
            controlsList="nodownload noremoteplayback"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            controls={isPlaying}
            poster="https://static.bancointer.com.br/payroll-loan/cp-refinanciamento-consignado-ui/thumbnail.jpg"
            preload="metadata"
            playsInline
          >
            <source
              src="https://static.bancointer.com.br/payroll-loan/inss-biometria.mp4"
              type="video/mp4"
            />
            <track kind="captions" />
          </StyledVideo>
          {!isPlaying && (
            <PlayButton type="button" aria-label="Reproduzir vídeo" onClick={handlePlay}>
              &#9654;
            </PlayButton>
          )}
        </VideoWrapper>

        <InstructionList>
          {visibleInstructions.map((item) => (
            <InstructionItem key={item.id}>
              <span className="number">{item.id}</span>
              {item.text}
            </InstructionItem>
          ))}
        </InstructionList>

        <ShowMoreText
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={expanded ? 'Mostrar menos dicas' : 'Mostrar mais dicas'}
          onClick={handleExpand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleExpand()
            }
          }}
        >
          {expanded ? 'Mostrar menos' : 'Mostrar mais'}
          {expanded ? <ArrowDown>&rsaquo;</ArrowDown> : <ArrowUp>&rsaquo;</ArrowUp>}
        </ShowMoreText>

        <Alert type="grayscale">
          Após o desbloqueio, retorne ao SuperApp do Inter e continue a sua contratação.
        </Alert>
      </Content>
    </Page>
  )
}

export default BeneficioBloqueado
